import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  Pagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import DeleteIcon from "@mui/icons-material/Delete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { tokens } from "../components/Theme";
import { createMerchant } from "../api/merchant/Create_merchant";
import { deleteMerchant } from "../api/merchant/Delete_merchant";
import { getMerchants } from "../api/merchant/GetAll_merchant";
import { updateMerchant } from "../api/merchant/Update_merchant";

function Merchant() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const userData = JSON.parse(localStorage.getItem("userdata")) || {};
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const defaultRole = userData.level === 1 ? "admin" : "user";
  const [role] = useState(defaultRole);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const rowsPerPage = 5;

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const confirmDelete = (index) => {
    setDeleteIndex(index);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    handleDelete(deleteIndex);
    setConfirmOpen(false);
    setDeleteIndex(null);
  };

  useEffect(() => {
    const fetchData = async () => {
      const res = await getMerchants();
      if (res.status === 200) {
        setData(res.payload);
      }
    };
    fetchData();
  }, []);

  const handleChange = (index, key, value) => {
    const updated = [...data];
    updated[index][key] = value;
    setData(updated);
  };

  const handleAdd = () => {
    const timestamp = Math.floor(Date.now() / 1000);
    const appMerchantId = `MC${timestamp}`;

    const newItem = {
      appMerchantId,
      province: "",
      amphur: "",
      tambon: "",
      postCode: "",
      merchantName: "",
      addr: "",
      phone: "",
    };
    setData((prev) => [...prev, newItem]);
  };

  const handleDelete = async (index) => {
    try {
      const deleted = data[index];
      if (deleted?.id) {
        await deleteMerchant(deleted.id);
      }

      setData(data.filter((_, i) => i !== index));
      showSnackbar("ลบข้อมูลเรียบร้อย", "success");
    } catch {
      showSnackbar("เกิดข้อผิดพลาดในการลบ", "error");
    }
  };

  const handleSave = async () => {
    try {
      for (const item of data) {
        const payload = { ...item };

        if (item.id) {
          await updateMerchant(item.id, payload);
        } else {
          console.log(payload)
          await createMerchant(payload);
          
        }
      }

      showSnackbar("บันทึกข้อมูลสำเร็จ", "success");

      const refreshed = await getMerchants();
      if (refreshed.status === 200) {
        setData(refreshed.payload);
      }
    } catch (error) {
      showSnackbar("เกิดข้อผิดพลาดในการบันทึก", "error");
    }
  };

  const paginatedData = data.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const fieldKeys = [
    "province",
    "amphur",
    "tambon",
    "postCode",
    "merchantName",
    "addr",
    "phone",
  ];

  const fieldLabels = {
    province: "จังหวัด",
    amphur: "อำเภอ",
    tambon: "ตำบล",
    postCode: "รหัสไปรษณีย์",
    merchantName: "ชื่อผู้ประกอบการ",
    addr: "ที่อยู่",
    phone: "เบอร์โทร",
  };

  return (
    <Box p={2}>
      <Typography variant="h6" mb={2}>
        ข้อมูลผู้ประกอบการ (ล้ง)
      </Typography>

      {isMobile ? (
        paginatedData.map((row, index) => {
          const realIndex = (page - 1) * rowsPerPage + index;
          return (
            <Accordion key={realIndex}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>
                  {row.merchantName || "ไม่มีชื่อผู้ประกอบการ"}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {fieldKeys.map((key) => (
                  <TextField
                    key={key}
                    label={fieldLabels[key]}
                    fullWidth
                    margin="dense"
                    size="small"
                    value={row[key] || ""}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ readOnly: role !== "admin" }}
                    onChange={(e) => handleChange(index, key, e.target.value)}
                  />
                ))}
                {role === "admin" && (
                  <Button
                    variant="outlined"
                    color="error"
                    fullWidth
                    sx={{ mt: 1 }}
                    onClick={() => confirmDelete(realIndex)}
                  >
                    ลบรายการนี้
                  </Button>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })
      ) : (
        <TableContainer component={Paper} sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ลำดับ</TableCell>
                {fieldKeys.map((key) => (
                  <TableCell key={key}>{fieldLabels[key]}</TableCell>
                ))}
                {role === "admin" && <TableCell>ลบ</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row, index) => {
                const realIndex = (page - 1) * rowsPerPage + index;
                return (
                  <TableRow key={realIndex}>
                    <TableCell>{realIndex + 1}</TableCell>
                    {fieldKeys.map((key) => (
                      <TableCell key={key}>
                        <TextField
                          variant="standard"
                          InputProps={{
                            disableUnderline: true,
                            readOnly: role !== "admin",
                            sx: {
                              minWidth: "120px",
                              input: {
                                color: "#000",
                                WebkitTextFillColor: "#000",
                              },
                            },
                          }}
                          size="small"
                          type={key === "postCode" ? "number" : "text"}
                          value={row[key] || ""}
                          onChange={(e) =>
                            handleChange(realIndex, key, e.target.value)
                          }
                        />
                      </TableCell>
                    ))}
                    {role === "admin" && (
                      <TableCell>
                        <IconButton
                          color="error"
                          onClick={() => confirmDelete(realIndex)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Pagination
        sx={{ mt: "10px", marginX: isMobile ? 12 : 0 }}
        count={Math.ceil(data.length / rowsPerPage)}
        page={page}
        onChange={(e, value) => setPage(value)}
        color="primary"
      />

      {role === "admin" && (
        <Box mt={2} display="flex" justifyContent="space-between">
          <Button
            variant="contained"
            color="success"
            startIcon={<AddIcon />}
            onClick={handleAdd}
          >
            เพิ่มข้อมูล
          </Button>
          <Button variant="contained" color="primary" onClick={handleSave}>
            บันทึก
          </Button>
        </Box>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>ยืนยันการลบ</DialogTitle>
        <DialogContent>
          <Typography>คุณแน่ใจว่าต้องการลบรายการนี้หรือไม่?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>ยกเลิก</Button>
          <Button color="error" onClick={handleConfirmDelete}>
            ลบ
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Merchant;
